import { GoogleCalendarSettingsDto, UserPreferences } from '@shared/core/preferences'
import classNames from 'classnames'
import { TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'
import { formatDateInLocale, locales } from 'src/helpers/locales'
import { getTimezoneOptions } from 'src/helpers/timezones'
import { CalendarLockMark, LockedCalendarsCard } from 'src/modules/calendar/components'
import { GET_CALENDARS_CACHE_KEY, useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { useUpdateCalendar } from 'src/modules/calendar/hooks/use-update-calendar'
import { isCalendarLimitReached, usesLockedCalendarsCard } from 'src/modules/calendar/utils/helpers'
import { Button, FormattedText, FormControl, Icon, Loader, SelectField, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useDisplayTimezone } from 'src/modules/core/context'
import { usePlanData } from 'src/modules/payment/hooks/use-plan-data'
import { getCalendarsLimitType } from 'src/modules/payment/utils/helpers'
import { buildUpgradePlansPath } from 'src/modules/payment/utils/upgrade-funnel-navigation'
import { useUpdateUserPreferences, useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { CalendarDto } from 'src/types/calendar.dto'
import classes from './calendars-tab.module.scss'

const SAMPLE_DATE = new Date(new Date().getFullYear(), 11, 31)

const UNLOCK_CALENDARS_PATH = buildUpgradePlansPath({
  trigger: 'calendar_add_attempt',
  source_element: 'calendars_tab_unlock',
  source_page: 'calendars_tab',
})

const LOCKED_ROW_UPGRADE_PATH = buildUpgradePlansPath({
  trigger: 'calendar_add_attempt',
  source_element: 'calendars_tab_locked_row',
  source_page: 'calendars_tab',
})

export function CalendarsTab() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { timezone: displayTimezone } = useDisplayTimezone()
  const calendars = useCalendars({}, { sync: true })
  const profile = useProfile()
  const preferences = useUserPreferences()
  const updatePreferences = useUpdateUserPreferences()
  const updateCalendar = useUpdateCalendar()

  // Track calendar changes locally until form submission
  const [calendarChanges, setCalendarChanges] = useState<
    Map<string, { selected?: boolean; timezone_custom?: string | null }>
  >(new Map())

  // Helper function to map API field names to form field names
  const mapApiToFormValues = (localPrefs: UserPreferences, googleSettings: GoogleCalendarSettingsDto) => {
    const isCustomized = localPrefs.google_calendar_settings_customized
    const source = isCustomized ? localPrefs : googleSettings
    return {
      customize_google_calendar_settings: isCustomized,
      locale: source.locale || 'en',
      date_format: source.date_field_order || 'DMY',
      hour_format: source.hour_format || 'h24',
    }
  }

  const { control, watch, setValue, handleSubmit } = useForm({
    defaultValues: preferences.data
      ? mapApiToFormValues(preferences.data.user_preferences, preferences.data.google_calendar_settings)
      : {
          customize_google_calendar_settings: true,
          locale: 'en',
          date_format: 'DMY',
          hour_format: 'h24',
        },
  })

  const customizeGoogleCalendarSettings = watch('customize_google_calendar_settings')

  // Update form values when preferences data loads
  useEffect(() => {
    if (preferences.data) {
      const isCustomized = preferences.data.user_preferences.google_calendar_settings_customized
      const newValues = mapApiToFormValues(preferences.data.user_preferences, preferences.data.google_calendar_settings)

      setValue('customize_google_calendar_settings', isCustomized)
      setValue('locale', newValues.locale)
      setValue('date_format', newValues.date_format)
      setValue('hour_format', newValues.hour_format)
    }
  }, [preferences.data, setValue])

  // Update form values when customization toggle changes
  const handleCustomizationToggle = (isCustomized: boolean) => {
    if (preferences.data) {
      // When disabling customization, use Google Calendar settings as defaults
      // When enabling customization, use current local preferences
      const sourcePreferences = isCustomized
        ? preferences.data.user_preferences
        : preferences.data.google_calendar_settings

      const newValues = {
        customize_google_calendar_settings: isCustomized,
        locale: sourcePreferences.locale || 'en',
        date_format: sourcePreferences.date_field_order || 'DMY',
        hour_format: sourcePreferences.hour_format || 'h24',
      }

      setValue('customize_google_calendar_settings', newValues.customize_google_calendar_settings)
      setValue('locale', newValues.locale)
      setValue('date_format', newValues.date_format)
      setValue('hour_format', newValues.hour_format)
    }
  }

  const handleFormSubmit = async (formData: any) => {
    // Validate that at least one calendar is selected
    if (selectedCalendars.length === 0) {
      // You could show an error message here or prevent submission
      console.error('At least one calendar must be selected')
      return
    }

    // Update user preferences first
    const updateDto = {
      google_calendar_settings_customized: formData.customize_google_calendar_settings,
      locale: formData.locale,
      date_field_order: formData.date_format,
      hour_format: formData.hour_format,
    }

    try {
      await updatePreferences.mutateAsync(updateDto)

      // Then update calendars if there are any changes
      if (calendarChanges.size > 0 && calendars.data) {
        const calendarUpdates = Array.from(calendarChanges.entries())
          .map(([calendarId, changes]) => {
            const calendar = calendars.data!.find((c) => c.id === calendarId)
            if (!calendar) return null

            // Only include fields that have actually changed
            const updateData: any = { id: calendarId }
            if (changes.selected !== undefined && changes.selected !== calendar.selected) {
              updateData.selected = changes.selected
            }
            if (changes.timezone_custom !== undefined && changes.timezone_custom !== calendar.timezone_custom) {
              updateData.timezone_custom = changes.timezone_custom
            }

            // Only return update if there are actual changes
            return Object.keys(updateData).length > 1 ? updateData : null
          })
          .filter(Boolean)

        if (calendarUpdates.length > 0) {
          // Update calendars in parallel
          await Promise.all(calendarUpdates.map((update) => updateCalendar.mutateAsync(update)))
        }
      }

      // Clear calendar changes after successful submission
      setCalendarChanges(new Map())

      // Invalidate calendar cache to refresh data
      await queryClient.invalidateQueries(GET_CALENDARS_CACHE_KEY)
    } catch (error) {
      console.error('Failed to save preferences:', error)
    }
  }

  const subscriptionOptions = {
    enabled: !!profile.data?.billing_subscription_id,
  }
  const { currentPlan } = usePlanData({
    subscriptionOptions,
  })

  const isLoading = useMemo(
    () =>
      calendars.isLoading ||
      profile.isLoading ||
      preferences.isLoading ||
      updatePreferences.isLoading ||
      updateCalendar.isLoading,
    [
      calendars.isLoading,
      profile.isLoading,
      preferences.isLoading,
      updatePreferences.isLoading,
      updateCalendar.isLoading,
    ],
  )

  const calendarsLimitType = getCalendarsLimitType(currentPlan)

  // This cohort gets every calendar and has nothing to upgrade to, so no
  // calendar may be locked and no upgrade affordance may be offered.
  const hideBillingUi = Boolean(profile.data?.hide_billing_ui)
  const consolidateLockedCalendars = usesLockedCalendarsCard(profile.data)

  const sortedCalendars = useMemo(() => {
    return calendars.data
      ? [...calendars.data].sort((a, b) => {
          if (a.primary && !b.primary) return -1
          if (!a.primary && b.primary) return 1
          return (a.title || a.calendar_id || '').localeCompare(b.title || b.calendar_id || '')
        })
      : []
  }, [calendars.data])

  const localeOptions = useMemo(() => {
    return locales.map((locale) => ({
      label: locale.name,
      value: locale.tag,
    }))
  }, [])

  const localeValue = watch('locale')

  const dateFormatOptions = useMemo(() => {
    return [
      { label: formatDateInLocale(SAMPLE_DATE, localeValue, 'DMY'), value: 'DMY' },
      { label: formatDateInLocale(SAMPLE_DATE, localeValue, 'MDY'), value: 'MDY' },
      { label: formatDateInLocale(SAMPLE_DATE, localeValue, 'YMD'), value: 'YMD' },
    ]
  }, [localeValue])

  const timeFormatOptions = useMemo(() => {
    return [
      { label: '13:00', value: 'h24' },
      { label: '1:00PM', value: 'h12' },
    ]
  }, [])

  // Memoize timezone options per calendar to avoid expensive recalculations
  const timezoneOptionsCache = useMemo(() => {
    const cache = new Map<string, Array<{ label: string; value: string }>>()
    const baseOptions = getTimezoneOptions()

    calendars.data?.forEach((calendar) => {
      const calendarDefault = baseOptions.find((option) => option.value === calendar.timezone)
      const options = [...baseOptions] // Create a copy
      if (calendarDefault) {
        options.unshift({
          label: `Calendar Default (${calendarDefault.label})`,
          value: '',
        })
      }

      cache.set(calendar.id, options)
    })

    return cache
  }, [calendars.data])

  const getTimezoneOptionsForCalendar = useCallback(
    (calendar: CalendarDto) => {
      return timezoneOptionsCache.get(calendar.id) || []
    },
    [timezoneOptionsCache],
  )

  // Memoize calendar states to avoid recalculating on every render
  const calendarStates = useMemo(() => {
    const states = new Map<string, { selected: boolean; timezone_custom: string | null }>()

    calendars.data?.forEach((calendar) => {
      const changes = calendarChanges.get(calendar.id)
      states.set(calendar.id, {
        selected: changes?.selected !== undefined ? changes.selected : calendar.selected,
        timezone_custom: changes?.timezone_custom !== undefined ? changes.timezone_custom : calendar.timezone_custom,
      })
    })

    return states
  }, [calendars.data, calendarChanges])

  // Helper to get current calendar state (including pending changes)
  const getCurrentCalendarState = useCallback(
    (calendar: CalendarDto) => {
      return (
        calendarStates.get(calendar.id) || {
          selected: calendar.selected,
          timezone_custom: calendar.timezone_custom,
        }
      )
    },
    [calendarStates],
  )

  // Calculate selected calendars (including pending changes)
  const selectedCalendars = useMemo(() => {
    if (!calendars.data) return []
    return calendars.data.filter((calendar) => {
      const currentState = getCurrentCalendarState(calendar)
      return currentState.selected
    })
  }, [calendars.data, getCurrentCalendarState])

  const isCalendarLocked = (calendar: CalendarDto) =>
    !hideBillingUi &&
    isCalendarLimitReached(currentPlan, calendar, selectedCalendars) &&
    !getCurrentCalendarState(calendar).selected
  const lockedCalendarsCount = sortedCalendars.filter(isCalendarLocked).length

  // Calendar change handlers (local state only)
  const handleCalendarSelectionChange = useCallback((calendarId: string, selected: boolean) => {
    setCalendarChanges((prev) => {
      const existing = prev.get(calendarId)
      if (existing?.selected === selected) {
        return prev // No change, return same Map reference
      }

      const newChanges = new Map(prev)
      newChanges.set(calendarId, { ...existing, selected })
      return newChanges
    })
  }, [])

  const handleCalendarTimezoneChange = useCallback((calendarId: string, timezoneValue: string | undefined) => {
    const timezoneCustom = timezoneValue || null
    setCalendarChanges((prev) => {
      const existing = prev.get(calendarId)
      if (existing?.timezone_custom === timezoneCustom) {
        return prev // No change, return same Map reference
      }

      const newChanges = new Map(prev)
      newChanges.set(calendarId, { ...existing, timezone_custom: timezoneCustom })
      return newChanges
    })
  }, [])

  if (isLoading) {
    return <Loader isLoading={isLoading} />
  }

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={classes.wrapper}>
          <FormControl label="Settings mode" error={undefined}>
            <Controller
              control={control}
              name="customize_google_calendar_settings"
              render={({ field: { value } }) => (
                <div className={classes.generalSettingsMode}>
                  <label className={classes.generalSettingsModeItem}>
                    <input type="radio" checked={value === false} onChange={() => handleCustomizationToggle(false)} />
                    <span>Sync from Google Calendar</span>
                  </label>
                  <label className={classes.generalSettingsModeItem}>
                    <input type="radio" checked={value === true} onChange={() => handleCustomizationToggle(true)} />
                    <span>Customize your settings</span>
                  </label>
                </div>
              )}
            />
          </FormControl>

          <FormControl label="Locale" error={undefined}>
            <Controller
              control={control}
              name="locale"
              render={({ field: { onChange, value } }) => (
                <SelectField
                  closeMenuOnSelect
                  variant="small"
                  placeholder="Select locale"
                  options={localeOptions}
                  value={localeOptions.find((option) => option.value === value)}
                  onChange={(option) => onChange(option?.value)}
                  isDisabled={!customizeGoogleCalendarSettings}
                />
              )}
            />
          </FormControl>

          <FormControl label="Date format" error={undefined}>
            <Controller
              control={control}
              name="date_format"
              render={({ field: { onChange, value } }) => (
                <SelectField
                  closeMenuOnSelect
                  variant="small"
                  placeholder="Select date format"
                  options={dateFormatOptions}
                  value={dateFormatOptions.find((option) => option.value === value)}
                  onChange={(option) => onChange(option?.value)}
                  isDisabled={!customizeGoogleCalendarSettings}
                />
              )}
            />
          </FormControl>

          <FormControl label="Time format" error={undefined}>
            <Controller
              control={control}
              name="hour_format"
              render={({ field: { onChange, value } }) => (
                <SelectField
                  closeMenuOnSelect
                  variant="small"
                  placeholder="Select time format"
                  options={timeFormatOptions}
                  value={timeFormatOptions.find((option) => option.value === value)}
                  onChange={(option) => onChange(option?.value)}
                  isDisabled={!customizeGoogleCalendarSettings}
                />
              )}
            />
          </FormControl>

          <div className={classes.configureCalendarsSection}>
            <Typography className={classes.title} variant="body">
              Select which calendars you want to sync
            </Typography>
            <Typography className={classes.description} variant="body">
              {hideBillingUi ? (
                <FormattedText id="calendars.description.free" />
              ) : (
                <FormattedText
                  id="calendars.description"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    limitType: calendarsLimitType,
                    limit: currentPlan?.calendars_limit,
                    isNew: false,
                  }}
                />
              )}
            </Typography>

            {consolidateLockedCalendars && lockedCalendarsCount > 0 && (
              <LockedCalendarsCard
                variant="settings"
                lockedCount={lockedCalendarsCount}
                upgradePath={UNLOCK_CALENDARS_PATH}
                className={classes.lockedCard}
              />
            )}

            {profile.data?.use_event_timezone && (
              <div className={classes.alertMessage}>
                <span>
                  You&apos;ve enabled{' '}
                  <a
                    href="/dashboard/settings?tab=other-settings"
                    className={classes.alertLink}
                    onClick={(e) => {
                      e.preventDefault()
                      navigate('/dashboard/settings?tab=other-settings')
                    }}
                  >
                    sending reminders to customers in different time zones
                  </a>
                  . The app will use the time zone settings below unless you set a specific time zone in the
                  calendar&apos;s event.
                </span>
              </div>
            )}

            <div className={classes.calendarListWrapper}>
              <ul className={classes.calendarList}>
                {sortedCalendars.map((calendar) => {
                  const currentState = getCurrentCalendarState(calendar)
                  const limitReached = isCalendarLocked(calendar)
                  const disabled = updateCalendar.isLoading || limitReached
                  const showLockedRow = limitReached && consolidateLockedCalendars
                  const primaryLabel = calendar.primary && (
                    <span className={classes.primaryLabel}>Primary Calendar</span>
                  )

                  return (
                    <li key={calendar.id} className={classes.calendarListItem}>
                      <div className={classes.calendarListItemHeader}>
                        <div className={classes.calendarListItemHeaderSelection}>
                          {showLockedRow ? (
                            <CalendarLockMark />
                          ) : (
                            <input
                              type="checkbox"
                              checked={currentState.selected}
                              disabled={disabled}
                              className={classes.calendarCheckbox}
                              onChange={(e) => {
                                handleCalendarSelectionChange(calendar.id, e.target.checked)
                              }}
                            />
                          )}
                          <span
                            className={classNames(classes.calendarName, {
                              [classes.calendarNameLocked]: showLockedRow,
                            })}
                          >
                            {calendar.title}
                          </span>
                        </div>
                        {showLockedRow ? (
                          <div className={classes.lockedRowActions}>
                            {primaryLabel}
                            <span className={classes.lockedLabel}>
                              <FormattedText id="calendars.locked.badge" />
                            </span>
                            <Link to={LOCKED_ROW_UPGRADE_PATH} className={classes.upgradeToSyncLink}>
                              <FormattedText id="calendars.locked.upgradeToSync" />
                            </Link>
                          </div>
                        ) : (
                          primaryLabel
                        )}
                      </div>

                      {currentState.selected && (
                        <div className={classes.calendarListItemContent}>
                          <SelectField
                            closeMenuOnSelect
                            variant="small"
                            value={getTimezoneOptionsForCalendar(calendar).find(
                              (option) => option.value === (currentState.timezone_custom || ''),
                            )}
                            options={getTimezoneOptionsForCalendar(calendar)}
                            placeholder="Select timezone"
                            onChange={(option) => {
                              handleCalendarTimezoneChange(calendar.id, option?.value)
                            }}
                            isSearchable={true}
                          />
                          {(currentState.timezone_custom?.trim() || calendar.timezone) !== displayTimezone && (
                            <div className={classes.timezoneMismatchAlert} role="alert">
                              <TriangleAlert className={classes.timezoneMismatchAlertIcon} aria-hidden />
                              <span>
                                This calendar&apos;s time zone differs from your display time zone. Reminder messages
                                may show a different time than what you see in the app. You can change the
                                calendar&apos;s time zone in Google Calendar or override it above.
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {limitReached && !consolidateLockedCalendars && (
                        <Typography
                          variant="body"
                          className={classes.upgradeButton}
                          onClick={() => navigate(`${routeNames.settings.path}?tab=plan`)}
                        >
                          <FormattedText id="calendars.dashboard.button" />
                        </Typography>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
          <div className={classes.defaultSettingsNotice}>
            <Icon name="info" />
            <span>Calendar Default keeps your timezone in sync with your Google Calendar settings.</span>
          </div>

          <div className={classes.submitSection}>
            <Button
              type="submit"
              variant="primary"
              disabled={updatePreferences.isLoading || selectedCalendars.length === 0}
              isLoading={updatePreferences.isLoading}
            >
              Save Preferences
            </Button>
            {selectedCalendars.length === 0 && (
              <Typography variant="body" color="error">
                At least one calendar must be selected to save preferences.
              </Typography>
            )}
            {updatePreferences.error && (
              <Typography variant="body" color="error">
                Failed to save preferences. Please try again.
              </Typography>
            )}
            {updatePreferences.isSuccess && (
              <Typography variant="body" color="success">
                Preferences saved successfully!
              </Typography>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}
